import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  sendEmailVerification
} from 'firebase/auth';
import { getFirebaseAuth } from '../config/firebase';
import { getActionCodeSettings, AUTH_ACTIONS } from '../config/authConfig';
import { initiatePasswordReset } from '../services/authService';

// Inactivity timeout duration (30 minutes)
const INACTIVITY_TIMEOUT = 30 * 60 * 1000;
let inactivityTimeout;

export const setupActivityListeners = () => {
  const resetTimer = () => {
    if (inactivityTimeout) {
      clearTimeout(inactivityTimeout);
    }
    
    inactivityTimeout = setTimeout(async () => {
      try {
        const auth = await getFirebaseAuth();
        await signOut(auth);
        
        // Clear any cached data for security
        try {
          sessionStorage.clear();
          localStorage.removeItem('appLogoUrl');
          console.log('User signed out due to inactivity - cleared session data');
        } catch (storageError) {
          console.warn('Error clearing storage on inactivity logout:', storageError);
        }
        
        // Show user-friendly message
        alert('You have been automatically logged out due to inactivity for security reasons.');
        
        // Redirect to login page
        window.location.href = '/login';
      } catch (error) {
        console.error('Error signing out:', error);
      }
    }, INACTIVITY_TIMEOUT);
  };

  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
  events.forEach(event => {
    document.addEventListener(event, resetTimer, { passive: true });
  });

  // Initial setup of the timer
  resetTimer();

  // Return cleanup function
  return () => {
    events.forEach(event => {
      document.removeEventListener(event, resetTimer);
    });
    if (inactivityTimeout) {
      clearTimeout(inactivityTimeout);
    }
  };
};

export const initializeAuth = async () => {
  try {
    const auth = await getFirebaseAuth();
    // Persistence is now configured in firebase config
    return true;
  } catch (error) {
    console.error('Error initializing auth:', error);
    return false;
  }
};

export const registerUser = async (email, password, displayName) => {
  try {
    const auth = await getFirebaseAuth();
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(userCredential.user, { displayName });
    
    // Send email verification with custom URL
    await sendEmailVerification(
      userCredential.user, 
      getActionCodeSettings(AUTH_ACTIONS.VERIFY_EMAIL)
    );
    
    return userCredential.user;
  } catch (error) {
    console.error('Error registering user:', error);
    throw error;
  }
};

export const loginUser = async (email, password) => {
  try {
    const auth = await getFirebaseAuth();
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    console.error('Error logging in user:', error);
    throw error;
  }
};

export const logout = async () => {
  try {
    const auth = await getFirebaseAuth();
    await signOut(auth);
    // Clear any inactivity timers
    if (inactivityTimeout) {
      clearTimeout(inactivityTimeout);
    }
    return true;
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

export const resetPassword = async (email) => {
  try {
    await initiatePasswordReset(email);
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
};

export const updateUserProfile = async (displayName) => {
  try {
    const auth = await getFirebaseAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('No user is currently signed in');
    await updateProfile(user, { displayName });
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

export const updateUserEmail = async (newEmail, password) => {
  try {
    const auth = await getFirebaseAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('No user is currently signed in');
    
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
    await updateEmail(user, newEmail);
  } catch (error) {
    console.error('Error updating user email:', error);
    throw error;
  }
};

export const updateUserPassword = async (currentPassword, newPassword) => {
  try {
    const auth = await getFirebaseAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('No user is currently signed in');
    
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  } catch (error) {
    console.error('Error updating user password:', error);
    throw error;
  }
};

export const getCurrentUser = async () => {
  try {
    const auth = await getFirebaseAuth();
    return auth.currentUser;
  } catch (error) {
    console.error('Error getting current user:', error);
    throw error;
  }
};

export const sendVerificationEmail = async () => {
  try {
    const auth = await getFirebaseAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('No user is currently signed in');
    
    await sendEmailVerification(
      user, 
      getActionCodeSettings(AUTH_ACTIONS.VERIFY_EMAIL)
    );
    
    return true;
  } catch (error) {
    console.error('Error sending verification email:', error);
    throw error;
  }
};
